/**
 * Prepara una foto del carrete para subirla.
 *
 * Tres motivos para reescalar en el cliente y no mandar el original:
 *
 *  1. **Formato.** Un iPhone guarda en HEIC, que el servidor no sabría mostrar
 *     a un navegador que no sea Safari. Al redibujar en un canvas sale JPEG,
 *     que entiende todo el mundo.
 *  2. **Tamaño.** Una foto de iPhone son 3–5 MB; reescalada a 1600 px baja a
 *     unos pocos cientos de kB. Subir por datos móviles deja de ser un problema.
 *  3. **Privacidad.** El canvas descarta los metadatos EXIF, y eso incluye las
 *     **coordenadas GPS** que el iPhone incrusta en cada foto. Son fotos de
 *     dentro de casa: no hay ninguna razón para que lleven la ubicación.
 */

const MAX_SIDE = 1600
const QUALITY = 0.82

async function decode(file: File): Promise<{ width: number; height: number; source: CanvasImageSource }> {
  // createImageBitmap es lo más directo y en Safari decodifica también HEIC.
  if ('createImageBitmap' in window) {
    try {
      const bitmap = await createImageBitmap(file)
      return { width: bitmap.width, height: bitmap.height, source: bitmap }
    } catch {
      // Sigue por el camino del <img>, más lento pero más compatible.
    }
  }

  const url = URL.createObjectURL(file)
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image()
      element.onload = () => resolve(element)
      element.onerror = () => reject(new Error('No se ha podido leer la imagen'))
      element.src = url
    })
    return { width: image.naturalWidth, height: image.naturalHeight, source: image }
  } finally {
    // Se revoca en el siguiente tick: revocarlo antes de dibujar dejaría el
    // canvas en blanco en algunos navegadores.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}

export async function prepareImage(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/') && !/\.(hei[cf]|jpe?g|png|webp)$/i.test(file.name)) {
    throw new Error('El archivo no es una imagen')
  }

  const { width, height, source } = await decode(file)
  if (width === 0 || height === 0) throw new Error('No se ha podido leer la imagen')

  const scale = Math.min(1, MAX_SIDE / Math.max(width, height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)

  const context = canvas.getContext('2d')
  if (!context) throw new Error('El navegador no permite procesar la imagen')
  context.drawImage(source, 0, 0, canvas.width, canvas.height)

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', QUALITY),
  )
  if (!blob) throw new Error('No se ha podido preparar la imagen')
  return blob
}

export async function uploadPhoto(instanceId: string, file: File, token: string | null): Promise<void> {
  const blob = await prepareImage(file)
  const body = new FormData()
  body.append('photo', blob, 'foto.jpg')

  const response = await fetch(`/api/instances/${instanceId}/photo`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body,
  })

  if (!response.ok) {
    const data = await response.json().catch(() => null)
    throw new Error(data?.message ?? 'No se ha podido subir la foto')
  }
}
